import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import { IEventInfo } from "@/props/IDonation";
import Countdown from "./Countdown";
import Image from "next/image";
import { formatUSD } from "@/utils";
import { useRouter } from "next/router";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

// ===== 常量区 =====
const ALLUSD = 50000;
const MAX_PERCENT = 94;
const MONTHLY_DEPOSIT = 5000;

// ===== 工具函数 =====
const getDonationProgress = (
  startTime: Date,
  endTime: Date,
  targetTotal: number,
  currentTime: Date = new Date(),
  monthlyDeposit: number = MONTHLY_DEPOSIT
): number => {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const now = new Date(currentTime);
  if (now <= start) return 0;
  if (now >= end) return targetTotal;
  // 1. 计算已经过了多少个完整月末
  let depositProgress = 0;
  const monthCursor = new Date(start);
  while (true) {
    const year = monthCursor.getFullYear();
    const month = monthCursor.getMonth();
    const lastDayOfMonth = new Date(year, month + 1, 0, 23, 59, 59, 999);
    if (now > lastDayOfMonth) {
      depositProgress += monthlyDeposit;
    } else {
      break;
    }
    // 下个月
    monthCursor.setMonth(month + 1);
    monthCursor.setDate(1);
  }
  // 2. 计算线性增长部分（精确到分钟）
  const linearTotal = Math.max(targetTotal - depositProgress, 0);
  // 将时间转换为分钟
  const totalMinutes = Math.floor((end.getTime() - start.getTime()) / (1000 * 60));
  const elapsedMinutes = Math.floor((now.getTime() - start.getTime()) / (1000 * 60));
  const linearProgress = (elapsedMinutes / totalMinutes) * linearTotal;
  // 3. 总进度（线性 + 月末注资），保留 2 位小数，不超过 targetTotal
  const totalProgress = depositProgress + linearProgress;
  return Math.min(parseFloat(totalProgress.toFixed(2)), targetTotal);
};

const formatTime = (date, format = "YYYY年MM月DD日 HH:mm:ss") => {
  if (!date) return "-";
  if (typeof date === "string" && date.includes("/")) {
    return dayjs(date, "DD/MM/YYYY HH:mm:ss").format(format);
  }
  return dayjs(date).format(format);
};

// EventProgress 项目进展
const EventProgress = ({ eventInfo, isMobile }: { eventInfo: IEventInfo; isMobile: boolean }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [progress, setProgress] = useState(0);
  // const [hasStarted, setHasStarted] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);

  const progressPercent = Math.floor((progress / ALLUSD) * 100);
  // 确保进度条百分比在合理范围内
  const safeAdjustedPercent = isMobile
    ? Math.max(12, Math.min(progressPercent, 48)) // mobile 的上下限
    : Math.max(4, Math.min(progressPercent, 50)); // pc 的上下限
  const minMaskPercent = isMobile ? 14 : 4;

  // 进度条和箭头都用这个
  const barWidthPercent = Math.max(progressPercent, minMaskPercent);

  useEffect(() => {
    if (!eventInfo?.startTime || !eventInfo?.endTime) return;
    const updateStatus = () => {
      const now = new Date().getTime();
      const start = new Date(eventInfo.startTime).getTime();
      const end = new Date(eventInfo.endTime).getTime();
      // setHasStarted(now >= start);
      setHasEnded(now >= end);
      const value = getDonationProgress(new Date(eventInfo.startTime), new Date(eventInfo.endTime), ALLUSD);
      setProgress(value);
    };
    updateStatus(); // 初始运行
    const timer = setInterval(updateStatus, 1000 * 60);
    return () => clearInterval(timer);
  }, [eventInfo.startTime, eventInfo.endTime]);

  const handleClickDonate = () => {
    router.push("#donate-now", undefined, { shallow: true });
  };

  return (
    <div className="relative">
      <Image
        src="/images/csr/countdown_2.webp"
        className="absolute left-0 top-0"
        width={isMobile ? 258 : 763}
        height={isMobile ? 48 : 141}
        alt={"countdown_2"}
      />
      <div className={`relative ${isMobile ? "pb-[90px]" : "pb-[141px]"}`}>
        <Image
          src="/images/csr/countdown_3.webp"
          width={isMobile ? 258 : 763}
          height={isMobile ? 48 : 141}
          alt="countdown_3"
          className="pointer-events-none absolute bottom-0 right-0 select-none"
        />
        <div className={`m-auto max-w-[1440px] text-center text-stone-800 ${isMobile ? "my-20 px-4" : "my-20"}`}>
          <h3
            className={`mb-4 font-semibold
            ${isMobile ? "h-[48px] text-2xl" : "h-[60px] text-5xl"}`}
          >
            {t("csr.event")}
            <span className="ml-1 inline-block bg-yellow-600 p-2">{t("csr.progress")}</span>
          </h3>
          <div
            className={`relative mb-10 flex items-center overflow-hidden rounded-[32px] shadow ${
              isMobile ? "mt-4 h-[296px] flex-col py-8" : "mt-[68px] h-[200px] flex-row py-10"
            }`}
          >
            <div className="absolute inset-0 -z-10">
              <Image
                src={isMobile ? "/images/csr/countdown_bg_mobile.webp" : "/images/csr/countdown_1.webp"}
                alt="Countdown background"
                fill
                className="object-cover"
                quality={100}
              />
            </div>
            <div className="flex-1 text-left">
              <p className={`px-20 font-bold ${isMobile ? "mb-4 text-center text-2xl" : "mb-[30px] text-4xl"}`}>
                {hasEnded ? t("csr.progress_end_desc") : t("csr.progress_desc")}
              </p>
              <p className={`text-[#4b5563] ${isMobile ? "px-4 text-center text-base" : "px-20 text-2xl"}`}>
                {formatTime(eventInfo?.startTime)} - {formatTime(eventInfo?.endTime)}（UTC+0）
              </p>
            </div>
            <div className={isMobile ? "" : "mr-20"}>
              <Countdown endTime={eventInfo?.endTime} isMobile={isMobile} />
            </div>
          </div>
          <div className="relative border-[2px] border-white">
            <div className="absolute -top-4 left-6 z-[-1] h-20 w-[calc(100%-48px)] overflow-hidden rounded-3xl bg-[#f0f0f0] shadow-[inset_0_0_0_1px_#e6e8eb]">
              <div
                className="-mr-6 h-full rounded-3xl bg-gradient-to-r from-[#f27477] to-[#d91d22] shadow transition-all duration-500"
                style={{ width: `${barWidthPercent}%` }}
              />
            </div>
            <div
              className={`relative z-10 rounded-3xl bg-white bg-gradient-to-b from-[#fff] to-[#fff0] p-6 shadow ${
                isMobile ? "" : "flex items-center justify-end"
              }`}
            >
              <div
                className={`pointer-events-none absolute left-0 top-0 z-10 h-[80px] bg-gradient-to-b from-[#fbe5e5] to-transparent ${
                  progressPercent > MAX_PERCENT ? "rounded-t-3xl" : "rounded-tl-3xl"
                }`}
                style={{
                  width: `calc(${Math.min(barWidthPercent, MAX_PERCENT)}% + 35px)`,
                  maskImage: "linear-gradient(to right, black 70%, transparent 100%)",
                  WebkitMaskImage: "linear-gradient(to right, black 70%, transparent 100%)",
                }}
              />
              <div
                className="absolute top-0 z-20"
                style={{
                  left: `${Math.min(barWidthPercent, isMobile ? MAX_PERCENT : 98)}%`,
                  transform:
                    barWidthPercent >= 50
                      ? `translateX(-${barWidthPercent >= 98 ? 12 : (18 * barWidthPercent) / 50}px)`
                      : "",
                }}
              >
                <Image
                  src="/images/csr/polygon.webp"
                  width={isMobile ? 16 : 25}
                  height={isMobile ? 13 : 19}
                  alt="polygon_icon"
                />
              </div>
              <div
                className="absolute z-30 flex items-center"
                style={{
                  left: `${safeAdjustedPercent}%`,
                  transform: "translateX(-35%)",
                }}
              >
                <span className={`${isMobile ? "text-sm" : "text-base"}`}>{t("csr.cur_progress")}：</span>
                <span className={`font-bold text-[#111827] ${isMobile ? "text-xl" : "text-2xl"}`}>
                  {progressPercent}%
                </span>
              </div>
              <div
                className={`flex items-center font-bold ${isMobile ? "mt-[56px] flex flex-col text-base" : "text-2xl"}`}
              >
                {/* 左侧文本 - 固定宽度 */}
                <span className="min-w-[200px] text-base text-[#111827]">{t("csr.donation_text")}</span>
                {/* 右侧数字 - 固定宽度 + 右对齐 */}
                <span className="mx-1 min-w-[350px] whitespace-nowrap text-center text-primary">
                  {formatUSD(progress ?? 0)}
                  <span className="ml-1 text-stone-800">/ {formatUSD(ALLUSD)} USD</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="text-black absolute bottom-[80px] left-1/2 inline-block -translate-x-1/2 cursor-pointer rounded-[30px] bg-yellow-600 px-12 py-3 text-lg font-semibold"
        onClick={handleClickDonate}
      >
        {t("csr.to_donate")}
      </div>
    </div>
  );
};
export default EventProgress;
